import React from 'react';
import {
  BoltIcon, FireIcon, TrophyIcon, UserGroupIcon, ShieldCheckIcon, StarIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import {
  FONTS, WA_NUMBER,
  HeroCarousel, BenefitsCTA,
  ActivitiesCTA, PhotoGallery, ReadyToStart,
} from './ServicePageTemplate';
import { Link } from 'react-router-dom';

// ─── IMPORT ONLY FITNESEQP IMAGES ─────────────────────────────────────────────
import Fitneseqp1 from '../../assets/images/Fitneseqp1.png';
import fitneseqp2 from '../../assets/images/fitneseqp2.png';
import fitneseqp3 from '../../assets/images/fitneseqp3.png';
import fitneseqp4 from '../../assets/images/fitneseqp4.png';
import fitneseqp5 from '../../assets/images/fitneseqp5.png';
import fitneseqp6 from '../../assets/images/fitneseqp6.png';
import fitneseqp7 from '../../assets/images/fitneseqp7.png';
import fitneseqp8 from '../../assets/images/fitneseqp8.png';
import fitneseqp9 from '../../assets/images/fitneseqp9.png';
import fitneseqp10 from '../../assets/images/fitneseqp10.png';
import fitneseqp11 from '../../assets/images/fitneseqp11.png';
import fitneseqp12 from '../../assets/images/fitneseqp12.png';

// ─── PHOTOS ARRAY FOR GALLERY (using fitneseqp images) ────────────────────────
const PHOTOS = [
  fitneseqp6, fitneseqp2, fitneseqp4, fitneseqp11,
  Fitneseqp1, fitneseqp3, fitneseqp5, fitneseqp7,
  fitneseqp8, fitneseqp9, fitneseqp10, fitneseqp12,
];


// ─── FEATURES DATA (with Boxing Training added) ───────────────────────────────────
const features = [
  { icon: <BoltIcon className="h-6 w-6" />, title: 'Free Weights Zone', desc: 'Dumbbells 2kg–50kg, barbells, EZ-bars and a full rack system. Whether you\'re isolating muscles or hammering compound lifts, the free weights zone is built for serious training.' },
  { icon: <FireIcon className="h-6 w-6" />, title: 'Cardio Suite', desc: 'Treadmills, rowing machines, assault bikes, stair climbers and ellipticals — all with performance tracking screens to keep you locked in the zone and pushing your limits.' },
  { icon: <TrophyIcon className="h-6 w-6" />, title: 'Strength Machines', desc: 'A full circuit of selectorised and plate-loaded machines targeting every muscle group. Perfect for building strength progressively, safely and with maximum muscle isolation.' },
  { icon: <UserGroupIcon className="h-6 w-6" />, title: 'Personal Training', desc: 'One-on-one sessions with certified personal trainers who design a program around your goals — weight loss, muscle gain, athletic performance or post-injury rehabilitation.' },
  { icon: <ShieldCheckIcon className="h-6 w-6" />, title: 'Functional Training Area', desc: 'Kettlebells, battle ropes, resistance bands, TRX suspension trainers, plyo boxes and more. Build real-world strength and explosive power that transfers beyond the gym.' },
  { icon: <StarIcon className="h-6 w-6" />, title: 'Olympic Lifting Platform', desc: 'Dedicated platform with bumper plates and Olympic bars for cleans, snatches, deadlifts and squats. Train like an elite athlete and perform like a champion.' },
  { 
    icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>, 
    title: 'Boxing Training', 
    desc: 'Professional boxing program featuring heavy bags, speed bags, double-end bags and a dedicated boxing ring. Train with our experienced boxing coach to improve cardio, hand-eye coordination, footwork, and overall conditioning while learning proper technique and defensive skills.' 
  },
];

// Custom Features Grid with Background Image (using fitneseqp5)
const FeaturesGridWithBackground = () => {
  return (
    <section className="py-16 relative overflow-hidden">
      {/* Background Image with fitneseqp5 */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${fitneseqp5})`,
        }}
      />
      
      {/* Dark overlay for better text visibility */}
      <div className="absolute inset-0 bg-black/50" />
      
      <div className="container-custom relative z-10">
        <div className="text-center mb-12">
          <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block text-red-500">
            Facilities & Equipment
          </span>
          <h2 className="font-black text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
            EVERYTHING YOU NEED <span className="text-red-600">UNDER ONE ROOF</span>
          </h2>
          <p className="text-gray-200 max-w-2xl mx-auto">
            State-of-the-art equipment and dedicated zones for every type of training
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="rounded-3xl p-7 transition-all duration-300 group hover:-translate-y-2"
              style={{ 
                background: 'rgba(0,0,0,0.75)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110"
                style={{ background: 'rgba(220,38,38,0.2)', color: '#ef4444' }}>
                {feature.icon}
              </div>
              <h3 className="font-black text-white mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.2rem' }}>
                {feature.title.toUpperCase()}
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DATA = {
  tagline:   'FORGE YOUR STRONGEST SELF.',
  heroText: 'World-class equipment, expert trainers and a facility built to get you real results — whatever your goal.',
  waMessage: encodeURIComponent("Hi PowerCore! 💪 I'm interested in the Gym & Strength Training facility. Could you tell me more about membership options and training programs?"),

  benefits: [
    'Build lean muscle and increase your metabolic rate',
    'Improve bone density, joint stability and posture',
    'Boost energy levels and mental clarity daily',
    'Progressive overload tracking with trainer support',
    'Safe, clean and well-maintained equipment always',
    'Air-conditioned facility with motivating atmosphere',
    'Locker rooms, showers and changing facilities',
    'Flexible membership — daily, weekly or monthly',
  ],
};

const GymService = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
    <style>{FONTS}</style>
    
    {/* 1. Hero Slider - Using fitneseqp6, fitneseqp2, fitneseqp4, fitneseqp11 */}
    <HeroCarousel photos={[fitneseqp6, fitneseqp2, fitneseqp4, fitneseqp11]} {...DATA} />
    
    {/* 2. Photo Gallery - INSIDE THE GYM (second section after hero) */}
    <PhotoGallery photos={PHOTOS} title="INSIDE THE GYM" />
    
    {/* 3. Features Grid with Background Image - EVERYTHING YOU NEED UNDER ONE ROOF */}
    <FeaturesGridWithBackground />
    
    {/* 4. Benefits CTA */}
    <BenefitsCTA benefits={DATA.benefits} benefitsTitle="RESULTS YOU CAN SEE. STRENGTH YOU CAN FEEL."
      ctaTitle="READY TO START TODAY?" ctaText="Message us on WhatsApp for a free facility tour, answers to all your questions and help choosing the right membership plan."
      waMessage={DATA.waMessage} />
    
    {/* 5. Activities CTA */}
    <ActivitiesCTA />
    
    {/* 6. Personal Training & General Training Section */}
    <div className="py-16 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="text-primary font-semibold tracking-wider uppercase bg-primary/20 px-4 py-1.5 rounded-full inline-block mb-3 text-sm">
            Training Options
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            CHOOSE YOUR <span className="text-primary">TRAINING PATH</span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Whether you prefer one-on-one coaching or independent training with expert guidance, we have the perfect option for you
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Personal Training Card - using fitneseqp8 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105">
            <div className="relative h-56 overflow-hidden">
              <img 
                src={fitneseqp8} 
                alt="Personal Training at PowerCore Fitness" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <h3 className="text-2xl font-bold text-white">Personal Training</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-3xl">👤</span>
                <span className="text-primary font-semibold">One-on-One Coaching</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Personalized attention and custom plans</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Form correction and injury prevention</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Nutritional guidance and accountability</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Faster results with expert coaching</span>
                </li>
              </ul>
              <Link 
                to="/pricing" 
                className="block w-full text-center bg-primary text-white py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
              >
                View Pricing <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* General Training Card - using fitneseqp10 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl transform transition-all duration-300 hover:scale-105">
            <div className="relative h-56 overflow-hidden">
              <img 
                src={fitneseqp10} 
                alt="General Training at PowerCore Fitness" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <h3 className="text-2xl font-bold text-white">General Training</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-3xl">👥</span>
                <span className="text-secondary font-semibold">Group & Independent Training</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-secondary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Motivation from peers and trainer</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary font-bold text-lg">✓</span>
                  <span className="text-gray-700">More affordable than one-on-one</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Build community while you train</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary font-bold text-lg">✓</span>
                  <span className="text-gray-700">Full access to all gym equipment</span>
                </li>
              </ul>
              <Link 
                to="/pricing" 
                className="block w-full text-center bg-secondary text-white py-3 rounded-xl font-semibold hover:bg-secondary-dark transition-colors flex items-center justify-center gap-2"
              >
                View Pricing <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-300 text-sm">
            Both options include full access to our facilities, locker rooms, and all equipment.
            <br />
            General training includes group classes, open gym access, and trainer support during sessions.
          </p>
        </div>
      </div>
    </div>
    
    {/* 7. Ready to Start CTA */}
    <ReadyToStart />
  </div>
);

export default GymService;